import type {OrderADto} from '@cohort/admin-schemas/orders';
import PolygonIcon from '@cohort/merchants/assets/images/polygon.png';
import type {TableEmptyState} from '@cohort/merchants/components/Table';
import Table from '@cohort/merchants/components/Table';
import type {Network} from '@cohort/shared/schema/common/blockchain';
import {CurrencySpecs} from '@cohort/shared/schema/common/currency';
import {blockchainExplorerUrl} from '@cohort/shared/utils/blockchain';
import {formatDate} from '@cohort/shared/utils/format';
import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';

type StoreOrdersTableProps = {
  orders: Array<OrderADto>;
  isLoading: boolean;
  emptyState?: TableEmptyState;
};

const StoreOrdersTable: React.FC<StoreOrdersTableProps> = ({orders, isLoading, emptyState}) => {
  const {t} = useTranslation('pages', {
    keyPrefix: 'campaigns.campaign.activity.store.storeOrdersTable',
  });

  const handleAddressClick = useCallback((network: Network, address: string) => {
    const baseUrl = blockchainExplorerUrl(network);
    window.open(`${baseUrl}/address/${address}#tokentxnsErc1155`, '_blank');
  }, []);

  const headers = [
    {label: t('tableHeaderDigitalAsset'), width: '200px'},
    {label: t('tableHeaderUser')},
    {label: t('tableHeaderDate')},
    {label: t('tableHeaderOrderId')},
    {label: t('tableHeaderPaidPrice')},
  ];

  const getPrice = (order: OrderADto): string => {
    if (order.amount === 0 || order.currency === null) {
      return '-';
    }
    const amount = CurrencySpecs.toDecimal(order.currency, order.amount);
    return `${amount} ${order.currency.toUpperCase()}`;
  };

  return (
    <Table headers={headers} isLoading={isLoading} emptyState={emptyState}>
      {orders.map(order => (
        <tr key={order.id} className="py-2">
          <td data-testid="order-id" className="py-4 pr-3 text-sm font-medium">
            #{order.tokenId}
          </td>
          <td
            data-testid="order-email"
            className="mx-auto flex items-center gap-2 whitespace-nowrap py-4 pr-3 text-sm text-slate-500"
          >
            {order.digitalAsset?.nft?.network !== undefined &&
              order.user.walletAddress !== null && (
                <button
                  data-testid="order-trx-address"
                  onClick={() =>
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    handleAddressClick(order.digitalAsset!.nft!.network, order.user.walletAddress!)
                  }
                >
                  <img src={PolygonIcon} alt="Polygon" className="h-4 w-4" />
                </button>
              )}
            {order.user.email}
          </td>
          <td
            data-testid="order-date"
            className="mx-auto whitespace-nowrap py-4 pr-3 text-sm text-slate-500"
          >
            {formatDate(order.createdAt)}
          </td>
          <td
            data-testid="order-order-id"
            className="mx-auto whitespace-nowrap py-4 pr-3 text-sm text-slate-500"
          >
            {order.id}
          </td>
          <td
            data-testid="order-price"
            className="mx-auto whitespace-nowrap py-4 pr-3 text-sm text-slate-500"
          >
            {getPrice(order)}
          </td>
        </tr>
      ))}
    </Table>
  );
};

export default StoreOrdersTable;
